// 数据转换函数，用于API模型和UI模型之间的转换

import {
  ApiSection,
  ApiGroup,
  ApiMemo,
  ApiMemoContent,
  ApiTag,
  ApiTagItem,
  ApiMemoTag,
  ApiShare,
  ApiShareInfo,
  ApiShareStatus,
  ApiUserGroup,
  ApiRepresentation,
  ApiRepresentationList
} from './ApiShapes';
import { UISection, UIGroup, UIMemo, UIMemoContent, UITag, UITagItem, UIMemoTag, UIShareInfo, UIShareStatus, UIUserGroup, UIRepresentation, UIRepresentationList, StateCode } from './UiShapes';
import { DataStatus } from '../common/MetaData';

// Section 相关转换函数
export function apiSectionToUISection(apiSection: ApiSection): UISection {
  return {
    ...apiSection,
    shareTypes: apiSection.shareTypes || [],
    updateStatus: DataStatus.UPDATED,
    isExpanded: false,
    isSelected: false
  };
}

export function uiSectionToApiSection(uiSection: UISection): ApiSection {
  const { updateStatus, isExpanded, isSelected, ...apiSection } = uiSection;
  return apiSection;
}

export function apiSectionsToUISections(apiSections: ApiSection[]): UISection[] {
  return apiSections.map(apiSectionToUISection);
}

// Group 相关转换函数
export function apiGroupToUIGroup(apiGroup: ApiGroup): UIGroup {
  return {
    ...apiGroup,
    shareTypes: apiGroup.shareTypes || [],
    updateStatus: DataStatus.UPDATED,
    memoCount: 0,
    isExpanded: false,
    isSelected: false
  };
}

export function uiGroupToApiGroup(uiGroup: UIGroup): ApiGroup {
  const { updateStatus, memoCount, isExpanded, isSelected, ...apiGroup } = uiGroup;
  return apiGroup;
}

export function apiGroupsToUIGroups(apiGroups: ApiGroup[]): UIGroup[] {
  return apiGroups.map(apiGroupToUIGroup);
}

// Memo 相关转换函数
export function apiMemoToUIMemo(apiMemo: ApiMemo): UIMemo {
  return {
    ...apiMemo,
    shareTypes: apiMemo.shareTypes || [],
    updateStatus: DataStatus.UPDATED,
    isSelected: false,
    isEditing: false
  };
}

export function uiMemoToApiMemo(uiMemo: UIMemo): ApiMemo {
  const { updateStatus, hit, isSelected, isEditing, ...apiMemo } = uiMemo;
  return apiMemo;
}

export function apiMemosToUIMemos(apiMemos: ApiMemo[]): UIMemo[] {
  return apiMemos.map(apiMemoToUIMemo);
}

// MemoContent 相关转换函数
/** @deprecated 内容已迁移至CRDT，MemoContent转换函数不再需要 */
export function apiMemoContentToUIMemoContent(apiMemoContent: ApiMemoContent, memoId: string): UIMemoContent {
  return {
    memoId,
    content: apiMemoContent.content
  };
}

/** @deprecated 内容已迁移至CRDT，MemoContent转换函数不再需要 */
export function uiMemoContentToApiMemoContent(uiMemoContent: UIMemoContent, userId: string): ApiMemoContent {
  return {
    userId,
    guid: uiMemoContent.memoId,
    createTimestamp: 0,
    modifyTimestamp: 0,
    stateCode: 1,
    content: uiMemoContent.content
  };
}

// Tag 相关转换函数
export function apiTagToUITag(apiTag: ApiTag): UITag {
  return {
    ...apiTag,
    usageCount: 0
  };
}

export function uiTagToApiTag(uiTag: UITag): ApiTag {
  const { usageCount, ...apiTag } = uiTag;
  return apiTag;
}

export function apiTagsToUITags(apiTags: ApiTag[]): UITag[] {
  return apiTags.map(apiTagToUITag);
}

// TagItem 相关转换函数
export function apiTagItemToUITagItem(apiTagItem: ApiTagItem): UITagItem {
  return {
    ...apiTagItem,
    level: parseInt(apiTagItem.level) || 50, // 将字符串level转换为数字
    color: getTagColor(apiTagItem.label)
  };
}

export function uiTagItemToApiTagItem(uiTagItem: UITagItem): ApiTagItem {
  const { color, ...apiTagItem } = uiTagItem;
  return {
    ...apiTagItem,
    level: uiTagItem.level.toString() // 将数字level转换为字符串
  };
}

// MemoTag 相关转换函数
export function apiMemoTagToUIMemoTag(apiMemoTag: ApiMemoTag, memoId: string): UIMemoTag {
  return {
    memoId,
    tags: apiMemoTag.tags.map(apiTagItemToUITagItem),
    lastUpdated: Date.now()
  };
}

export function uiMemoTagToApiMemoTag(uiMemoTag: UIMemoTag): ApiMemoTag {
  return {
    guid: uiMemoTag.memoId,
    tags: uiMemoTag.tags.map(uiTagItemToApiTagItem)
  };
}

// MemoShare 相关转换函数（更新为使用UIShareInfo接口）
export function apiMemoShareToUIShareInfo(apiMemoShare: any): UIShareInfo {
  return {
    targetType: parseInt(apiMemoShare.targetType) || 0,
    targetId: apiMemoShare.targetId || '',
    displayName: getTargetDisplayName(parseInt(apiMemoShare.targetType) || 0, apiMemoShare.targetId || ''),
    isExpanded: false
  };
}

export function uiShareInfoToApiMemoShare(uiShareInfo: UIShareInfo): any {
  return {
    targetType: uiShareInfo.targetType.toString(),
    targetId: uiShareInfo.targetId || '',
    targetName: uiShareInfo.displayName || ''
  };
}

// ShareInfo 相关转换函数
// ShareInfo 相关转换函数（统一处理所有分享类型）
export function apiShareInfoToUIShareInfo(apiShareInfo: ApiShareInfo): UIShareInfo {
  return {
    targetType: apiShareInfo.targetType || 0,
    targetId: apiShareInfo.targetId || '',
    displayName: getTargetDisplayName(apiShareInfo.targetType || 0, apiShareInfo.targetId),
    isExpanded: false
  };
}

export function uiShareInfoToApiShareInfo(uiShareInfo: UIShareInfo): ApiShareInfo {
  return {
    targetType: uiShareInfo.targetType || 0,
    targetId: uiShareInfo.targetId || '',
    targetName: uiShareInfo.displayName || '',
    createTimestamp: 0,
    modifyTimestamp: 0,
    isPublic: false,
    targetUserGroupIds: [],
    targetUserIds: [],
    sectionId: '',
    groupId: '',
    memoId: ''
  };
}

export function apiShareInfosToUIShareInfos(apiShareInfos: ApiShareInfo[]): UIShareInfo[] {
  return apiShareInfos.map(apiShareInfoToUIShareInfo);
}

// ApiShareStatus到UIShareStatus的转换函数
export function apiShareStatusToUIShareStatus(apiShareStatus: ApiShareStatus): UIShareStatus {
  return {
    guid: apiShareStatus.guid,
    createTimestamp: apiShareStatus.createTimestamp,
    modifyTimestamp: apiShareStatus.modifyTimestamp,
    shareInfo: (apiShareStatus.shareInfo || []).map((apiShareInfo: ApiShareInfo) => ({
      targetType: typeof apiShareInfo.targetType === 'string' ? parseInt(apiShareInfo.targetType) : apiShareInfo.targetType,
      targetId: apiShareInfo.targetId,
      displayName: getTargetDisplayName(typeof apiShareInfo.targetType === 'string' ? parseInt(apiShareInfo.targetType) : apiShareInfo.targetType, apiShareInfo.targetId)
    }))
  };
}

// UIShareStatus到ApiShareInfo的转换函数
export function uiShareStatusToApiShareInfo(uiShareStatus: UIShareStatus): ApiShareInfo {
  // 使用第一个shareInfo项作为基础
  const firstShareInfo = uiShareStatus.shareInfo && uiShareStatus.shareInfo.length > 0 ? uiShareStatus.shareInfo[0] : ({} as UIShareInfo);

  return {
    targetType: firstShareInfo.targetType || 0,
    targetId: firstShareInfo.targetId || '',
    targetName: firstShareInfo.displayName || '',
    createTimestamp: uiShareStatus.createTimestamp || 0,
    modifyTimestamp: uiShareStatus.modifyTimestamp || 0,
    isPublic: false,
    targetUserGroupIds: [],
    targetUserIds: [],
    sectionId: '',
    groupId: '',
    memoId: ''
  };
}

// 辅助函数：根据ApiShareInfo确定类型标记
function getTypeMark(apiShareInfo: ApiShareInfo): number {
  if (apiShareInfo.sectionId) return 2; // SECTION类型标记
  if (apiShareInfo.groupId) return 3; // GROUP类型标记
  if (apiShareInfo.memoId) return 1; // MEMO类型标记
  return 0; // 默认类型
}

// 辅助函数：根据ApiShareInfo确定guid
function getGuid(apiShareInfo: ApiShareInfo): string {
  return apiShareInfo.sectionId || apiShareInfo.groupId || apiShareInfo.memoId || '';
}

// UserGroup 相关转换函数
export function apiUserGroupToUIUserGroup(apiUserGroup: ApiUserGroup): UIUserGroup {
  return {
    ...apiUserGroup,
    userMarks:
      apiUserGroup.userMarks?.map((mark) => ({
        id: mark.id,
        label: mark.label
      })) || [],
    memberCount: apiUserGroup.userMarks?.length || 0
  };
}

export function uiUserGroupToApiUserGroup(uiUserGroup: UIUserGroup): ApiUserGroup {
  return {
    ...uiUserGroup,
    userMarks:
      uiUserGroup.userMarks?.map((mark) => ({
        id: mark.id,
        label: mark.label
      })) || []
  };
}

export function apiUserGroupsToUIUserGroups(apiUserGroups: ApiUserGroup[]): UIUserGroup[] {
  return apiUserGroups.map(apiUserGroupToUIUserGroup);
}

// Representation 相关转换函数
export function apiRepresentationToUIRepresentation(apiRepresentation: ApiRepresentation): UIRepresentation {
  return {
    ...apiRepresentation,
    isVisible: true
  };
}

export function uiRepresentationToApiRepresentation(uiRepresentation: UIRepresentation): ApiRepresentation {
  const { isVisible, ...apiRepresentation } = uiRepresentation;
  return apiRepresentation;
}

// RepresentationList 相关转换函数
export function apiRepresentationListToUIRepresentationList(apiRepresentationList: ApiRepresentationList): UIRepresentationList {
  return {
    ...apiRepresentationList,
    representations: apiRepresentationList.representations.map(apiRepresentationToUIRepresentation)
  };
}

export function uiRepresentationListToApiRepresentationList(uiRepresentationList: UIRepresentationList): ApiRepresentationList {
  return {
    ...uiRepresentationList,
    representations: uiRepresentationList.representations.map(uiRepresentationToApiRepresentation)
  };
}

// 工具函数
function getTagColor(label: string): string {
  // 根据标签名称生成一致的颜色
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function getTargetDisplayName(targetType: number, targetId: string): string {
  // 根据目标类型和ID生成显示名称
  // targetType 编码与服务端 ShareSettingType 严格对齐：0=NONE, 1=PUBLIC, 2=USER_GROUP, 3=USER
  switch (targetType) {
    case 0:
      // NONE：无共享，历史数据可能存在，统一显示为非公开
      return "非公开";
    case 1:
      // PUBLIC：公开分享，targetId为"true"表示公开，"false"表示非公开
      return targetId === "true" ? "公开" : "非公开";
    case 2:
      // USER_GROUP：用户组分享
      return `用户组: ${targetId}`;
    case 3:
      // USER：用户分享
      return `用户: ${targetId}`;
    default:
      return `目标 ${targetId}`;
  }
}

// 导出默认对象，方便统一导入
export default {
  // Section转换
  apiSectionToUISection,
  uiSectionToApiSection,
  apiSectionsToUISections,
  // Group转换
  apiGroupToUIGroup,
  uiGroupToApiGroup,
  apiGroupsToUIGroups,
  // Memo转换
  apiMemoToUIMemo,
  uiMemoToApiMemo,
  apiMemosToUIMemos,
  // MemoContent转换
  apiMemoContentToUIMemoContent,
  uiMemoContentToApiMemoContent,
  // Tag转换
  apiTagToUITag,
  uiTagToApiTag,
  apiTagsToUITags,
  // TagItem转换
  apiTagItemToUITagItem,
  uiTagItemToApiTagItem,
  // MemoTag转换
  apiMemoTagToUIMemoTag,
  uiMemoTagToApiMemoTag,
  // ShareInfo转换
  apiShareInfoToUIShareInfo,
  uiShareInfoToApiShareInfo,
  // UserGroup转换
  apiUserGroupToUIUserGroup,
  uiUserGroupToApiUserGroup,
  apiUserGroupsToUIUserGroups,
  // Representation转换
  apiRepresentationToUIRepresentation,
  uiRepresentationToApiRepresentation,
  // RepresentationList转换
  apiRepresentationListToUIRepresentationList,
  uiRepresentationListToApiRepresentationList
};

// 导出工具函数
export { getTargetDisplayName };

/**
 * 从 shareInfo 列表计算有效的 hasShare 和 shareTypes
 * targetType 与服务端 ShareSettingType 对齐：0=NONE, 1=PUBLIC, 2=USER_GROUP, 3=USER
 * - targetType=0（NONE）不算有效共享
 * - targetType=1（PUBLIC）只有 targetId="true" 时才算有效共享
 * - targetType=2/3 只有 targetId 非空时才算有效共享
 */
export function computeShareStatus(shareInfoList: UIShareInfo[]): { hasShare: boolean; shareTypes: number[] } {
  const shareTypes: number[] = Array.from(new Set(
    shareInfoList
      .filter((info) => {
        if (typeof info.targetType !== 'number') return false;
        if (info.targetType === 0) return false; // NONE
        if (info.targetType === 1) return info.targetId === 'true'; // PUBLIC
        return !!(info.targetId && info.targetId.trim() !== '' && info.targetId !== '[]');
      })
      .map((info) => info.targetType)
  ));
  return { hasShare: shareTypes.length > 0, shareTypes };
}
